import {
  Input,
  OnInit,
  Injector,
  OnDestroy,
  Directive,
  Renderer2,
  ElementRef,
  ComponentRef,
  ApplicationRef,
  createComponent,
} from '@angular/core';

import { NgpPopoverPlacement } from 'ng-primitives/popover';

import { HelpService } from '../core/help.service';

import { HelpPanelComponent } from '../panel/help-panel.component';
import { HelpInlineComponent } from '../inline/help-inline.component';
import { HelpPopoverComponent } from '../popover/help-popover.component';

import { HelpMode, DimensionValue, HELP_DEFAULTS } from '../core/help.models';

// * Directive to mark elements as help anchors
@Directive({
  selector: '[helpAnchor]',
  standalone: false,
})
export class HelpDirective implements OnInit, OnDestroy {
  private static readonly UNSUPPORTED_HOST_TAGS = new Set([
    'input',
    'textarea',
    'select',
    'img',
    'video',
    'audio',
    'canvas',
    'iframe',
    'object',
    'embed',
    'area',
    'base',
    'br',
    'col',
    'hr',
    'source',
    'track',
    'wbr',
  ]);

  @Input() helpAnchor!: string;
  @Input() helpMode: HelpMode = HELP_DEFAULTS.MODE;
  @Input() helpPlacement?: NgpPopoverPlacement;
  @Input() helpPosition: 'append' | 'prepend' = 'append';
  @Input() helpTitle?: string;

  @Input() helpWidth?: DimensionValue;
  @Input() helpHeight?: DimensionValue;
  @Input() helpMinWidth?: DimensionValue;
  @Input() helpMinHeight?: DimensionValue;
  @Input() helpMaxWidth?: DimensionValue;
  @Input() helpMaxHeight?: DimensionValue;

  private anchorId?: string;

  private componentRef?: ComponentRef<
    HelpPopoverComponent | HelpPanelComponent | HelpInlineComponent
  >;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private injector: Injector,
    private appRef: ApplicationRef,
    private helpService: HelpService,
  ) {}

  ngOnInit(): void {
    if (!this.helpAnchor) {
      console.warn('HelpDirective: helpAnchor input is required');
      return;
    }

    const hostElement = this.elementRef.nativeElement;
    if (this.isUnsupportedHostElement(hostElement)) {
      console.warn(
        `HelpDirective: unsupported host element <${hostElement.tagName.toLowerCase()}>. Wrap it in a container and apply helpAnchor to the wrapper.`,
      );
      return;
    }

    // Register anchor with HelpService
    this.anchorId = this.helpService.registerAnchor(this.elementRef, {
      key: this.helpAnchor,
      mode: this.helpMode,
      placement: this.helpPlacement,
      title: this.helpTitle,
      width: this.helpWidth,
      height: this.helpHeight,
      minWidth: this.helpMinWidth,
      minHeight: this.helpMinHeight,
      maxWidth: this.helpMaxWidth,
      maxHeight: this.helpMaxHeight,
    });

    // Create appropriate component based on mode
    this.createComponent();
  }

  ngOnDestroy(): void {
    if (this.componentRef) {
      this.appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
    }

    if (this.anchorId) {
      this.helpService.unregisterAnchor(this.anchorId);
    }
  }

  private createComponent(): void {
    if (this.helpMode === 'popover') {
      // Create popover component
      const popoverRef = createComponent(HelpPopoverComponent, {
        environmentInjector: this.appRef.injector,
        elementInjector: this.injector,
      });

      popoverRef.instance.helpKey = this.helpAnchor;
      popoverRef.instance.anchorId = this.anchorId;
      popoverRef.instance.placement =
        this.helpPlacement || HELP_DEFAULTS.PLACEMENT;
      popoverRef.instance.title = this.helpTitle;
      popoverRef.instance.width = this.helpWidth;
      popoverRef.instance.height = this.helpHeight;
      popoverRef.instance.minWidth = this.helpMinWidth;
      popoverRef.instance.minHeight = this.helpMinHeight;
      popoverRef.instance.maxWidth = this.helpMaxWidth;
      popoverRef.instance.maxHeight = this.helpMaxHeight;

      this.appRef.attachView(popoverRef.hostView);
      const element = popoverRef.location.nativeElement as HTMLElement;
      this.insertElement(element);
      popoverRef.changeDetectorRef.detectChanges();

      this.componentRef = popoverRef;
    } else if (this.helpMode === 'panel') {
      // Create panel component
      const panelRef = createComponent(HelpPanelComponent, {
        environmentInjector: this.appRef.injector,
        elementInjector: this.injector,
      });

      panelRef.instance.helpKey = this.helpAnchor;
      panelRef.instance.anchorId = this.anchorId;
      panelRef.instance.title = this.helpTitle;
      panelRef.instance.width = this.helpWidth;
      panelRef.instance.height = this.helpHeight;
      panelRef.instance.minWidth = this.helpMinWidth;
      panelRef.instance.minHeight = this.helpMinHeight;
      panelRef.instance.maxWidth = this.helpMaxWidth;
      panelRef.instance.maxHeight = this.helpMaxHeight;

      this.appRef.attachView(panelRef.hostView);
      const element = panelRef.location.nativeElement as HTMLElement;
      this.insertElement(element);
      panelRef.changeDetectorRef.detectChanges();

      this.componentRef = panelRef;
    } else {
      // Create inline component
      const inlineRef = createComponent(HelpInlineComponent, {
        environmentInjector: this.appRef.injector,
        elementInjector: this.injector,
      });

      inlineRef.instance.helpKey = this.helpAnchor;
      inlineRef.instance.anchorId = this.anchorId;

      this.appRef.attachView(inlineRef.hostView);
      const element = inlineRef.location.nativeElement as HTMLElement;
      this.insertElementInside(element);
      inlineRef.changeDetectorRef.detectChanges();

      this.componentRef = inlineRef;
    }
  }

  private isUnsupportedHostElement(element: HTMLElement): boolean {
    return HelpDirective.UNSUPPORTED_HOST_TAGS.has(
      element.tagName.toLowerCase(),
    );
  }

  // * (append or prepend) -> default: append at the end
  private insertElement(element: HTMLElement): void {
    const hostElement = this.elementRef.nativeElement;

    if (this.helpPosition === 'prepend') {
      this.renderer.insertBefore(hostElement, element, hostElement.firstChild);
    } else {
      this.renderer.appendChild(hostElement, element);
    }
  }

  // * For inline mode - insert inside the host element at the end
  private insertElementInside(element: HTMLElement): void {
    const hostElement = this.elementRef.nativeElement;
    this.renderer.appendChild(hostElement, element);
  }
}
